/**
 * Respuestas de la API (SSOT S1). El SDK del front se deriva de aquí.
 */
import { z } from 'zod';
import { CentsWireSchema } from '../money.js';
import { paginated } from '../domain/common.js';
import { FightSchema, FightMetricsSchema, FinalMetricsSchema } from '../domain/fight.js';
import { BetSchema } from '../domain/bet.js';
import { PayoutSchema } from '../domain/payout.js';
import { PresetSchema } from '../domain/preset.js';
import { UserSchema, TerminalSchema, OrganizationSchema } from '../domain/org.js';
import { AuditEventSchema } from '../domain/audit.js';
import { HouseMetricsSchema, ArchivedFightSchema } from '../domain/metrics.js';
import { PermissionSchemaWire } from '../wire.js';

export const ServerInfoSchema = z.object({
  serverTime: z.string(),
  engineVersion: z.string(),
  apiVersion: z.string(),
});

/**
 * Vista PÚBLICA de la pelea (sin sesión). No incluye ciudadanos ni usuarios: los nombres
 * viajan enmascarados en `PublicTicket`. Es lo que ve un espectador del stream.
 */
export const PublicFightResponse = z.object({
  fight: FightSchema.nullable(),
  metrics: FightMetricsSchema.nullable(),
  server: ServerInfoSchema,
});
export type PublicFightResponse = z.infer<typeof PublicFightResponse>;

/** Boleto con el ciudadano enmascarado ("Tony R.") para no exponer datos personales. */
export const PublicTicket = z.object({
  receiptCode: z.string(),
  corner: z.enum(['red', 'blue']),
  citizenLabel: z.string().max(60),
  stakeCents: CentsWireSchema,
  oddsBp: z.number().int().positive().nullable(),
  createdAt: z.string().datetime(),
});

export type PublicTicket = z.infer<typeof PublicTicket>;

export const PublicTicketsResponse = z.object({
  items: z.array(PublicTicket).max(50),
});
export type PublicTicketsResponse = z.infer<typeof PublicTicketsResponse>;

/** El código se muestra UNA sola vez, acá. Luego solo queda el hash. */
export const AccessCodeRotatedResponse = z.object({
  code: z.string().min(8).max(64),
  rotatedAt: z.string().datetime(),
  previousRevoked: z.boolean(),
});
export type AccessCodeRotatedResponse = z.infer<typeof AccessCodeRotatedResponse>;

export const MeResponse = z.object({
  user: UserSchema,
  org: OrganizationSchema,
  permissions: z.array(PermissionSchemaWire),
  server: ServerInfoSchema,
});
export type MeResponse = z.infer<typeof MeResponse>;

export const OtpRequestedResponse = z.object({
  sent: z.literal(true),
  expiresInSeconds: z.number().int().positive(),
  /** Solo en desarrollo: el código, para poder probar sin proveedor de email. */
  devCode: z.string().optional(),
});
export type OtpRequestedResponse = z.infer<typeof OtpRequestedResponse>;

export const AuthSessionResponse = z.object({
  accessToken: z.string(),
  tokenType: z.literal('Bearer'),
  expiresIn: z.number().int().positive(),
  refreshToken: z.string().optional(),
  user: UserSchema,
  org: OrganizationSchema,
  permissions: z.array(PermissionSchemaWire),
  server: ServerInfoSchema,
});
export type AuthSessionResponse = z.infer<typeof AuthSessionResponse>;

export const CurrentFightResponse = z.object({
  fight: FightSchema.nullable(),
  metrics: FightMetricsSchema.nullable(),
  server: ServerInfoSchema,
});
export type CurrentFightResponse = z.infer<typeof CurrentFightResponse>;

export const FightResponse = z.object({
  fight: FightSchema,
  server: ServerInfoSchema,
});
export type FightResponse = z.infer<typeof FightResponse>;

export const FightsListResponse = paginated(FightSchema);
export type FightsListResponse = z.infer<typeof FightsListResponse>;

/** Recibo imprimible: SIEMPRE rotula el estimado y muestra la fórmula (§8.1.5). */
export const ReceiptSchema = z.object({
  receiptCode: z.string(),
  betId: z.string(),
  fightTitle: z.string(),
  corner: z.enum(['red', 'blue']),
  cornerLabel: z.string(),
  stakeCents: CentsWireSchema,
  oddsBp: z.number().int().nonnegative(),
  estPayoutCents: CentsWireSchema,
  /** 'fijo' = cuota congelada real; 'estimado' = pari-mutuel. */
  oddsKind: z.enum(['fijo', 'estimado']),
  formula: z.string(),
  printedAt: z.string(),
});
export type Receipt = z.infer<typeof ReceiptSchema>;

export const CreateBetResponse = z.object({
  bet: BetSchema,
  receipt: ReceiptSchema,
  metrics: FightMetricsSchema,
  /** true si la respuesta vino del replay de idempotencia. */
  replayed: z.boolean(),
});
export type CreateBetResponse = z.infer<typeof CreateBetResponse>;

export const BetsListResponse = paginated(BetSchema);
export type BetsListResponse = z.infer<typeof BetsListResponse>;

export const PayBetResponse = z.object({
  bet: BetSchema,
  payout: PayoutSchema,
  replayed: z.boolean(),
});
export type PayBetResponse = z.infer<typeof PayBetResponse>;

export const PendingPayoutsResponse = z.object({
  items: z.array(
    z.object({
      bet: BetSchema,
      payableCents: CentsWireSchema,
    }),
  ),
  totalCents: CentsWireSchema,
  count: z.number().int().nonnegative(),
});
export type PendingPayoutsResponse = z.infer<typeof PendingPayoutsResponse>;

export const SettleResponse = z.object({
  fight: FightSchema,
  finalMetrics: FinalMetricsSchema,
  summary: z.object({
    winners: z.number().int().nonnegative(),
    losers: z.number().int().nonnegative(),
    totalPaidCents: CentsWireSchema,
    houseResultCents: z.number().int(),
  }),
  replayed: z.boolean(),
});
export type SettleResponse = z.infer<typeof SettleResponse>;

export const SettleAllResponse = z.object({
  paidCount: z.number().int().nonnegative(),
  paidCents: CentsWireSchema,
  remainingCount: z.number().int().nonnegative(),
  payouts: z.array(PayoutSchema),
  replayed: z.boolean(),
});
export type SettleAllResponse = z.infer<typeof SettleAllResponse>;

export const HistoryResponse = paginated(ArchivedFightSchema);
export type HistoryResponse = z.infer<typeof HistoryResponse>;

export const HouseMetricsResponse = z.object({
  metrics: HouseMetricsSchema,
  server: ServerInfoSchema,
});
export type HouseMetricsResponse = z.infer<typeof HouseMetricsResponse>;

export const PresetsResponse = z.object({
  items: z.array(PresetSchema),
  pending: z.array(PresetSchema),
  server: ServerInfoSchema,
});
export type PresetsResponse = z.infer<typeof PresetsResponse>;

export const PresetResponse = z.object({
  preset: PresetSchema,
});
export type PresetResponse = z.infer<typeof PresetResponse>;

export const UsersListResponse = paginated(UserSchema);
export type UsersListResponse = z.infer<typeof UsersListResponse>;

export const UserResponse = z.object({
  user: UserSchema,
});
export type UserResponse = z.infer<typeof UserResponse>;

export const AuditListResponse = paginated(AuditEventSchema);
export type AuditListResponse = z.infer<typeof AuditListResponse>;

export const TerminalsResponse = z.object({
  items: z.array(
    TerminalSchema.extend({
      online: z.boolean(),
    }),
  ),
});
export type TerminalsResponse = z.infer<typeof TerminalsResponse>;

export const ExportResponse = z.object({
  format: z.enum(['txt', 'json', 'csv']),
  filename: z.string(),
  content: z.string(),
});
export type ExportResponse = z.infer<typeof ExportResponse>;

export const HealthResponse = z.object({
  status: z.literal('ok'),
  uptimeSeconds: z.number().nonnegative(),
  version: z.string(),
  /** healthz NO consulta la BD (directriz de costos §22). */
  dbChecked: z.literal(false),
});
export type HealthResponse = z.infer<typeof HealthResponse>;

export const ReadyResponse = z.object({
  status: z.enum(['ready', 'degraded']),
  cacheWarm: z.boolean(),
  engineVersion: z.string(),
  uptimeSeconds: z.number().nonnegative(),
});
export type ReadyResponse = z.infer<typeof ReadyResponse>;
